import { HttpClientConfig } from "../types/http-types";
import { apiConfigUtils } from "../../config";

/**
 * HTTP Client Configuration
 * Centralized configuration for all HTTP clients
 * Follows SOC: Separation of concerns - configuration separated from implementation
 */

/**
 * Default HTTP client configuration
 */
export const DEFAULT_HTTP_CONFIG: Omit<HttpClientConfig, "baseURL"> = {
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
};

/**
 * Get complete HTTP client configuration
 * Uses the centralized API URL configuration
 */
export function getHttpClientConfig(): HttpClientConfig {
  return {
    ...DEFAULT_HTTP_CONFIG,
    baseURL: apiConfigUtils.getBaseUrl(),
  };
}

/**
 * Configuration for streaming requests (longer timeout)
 */
export const STREAMING_HTTP_CONFIG: Omit<HttpClientConfig, "baseURL"> = {
  ...DEFAULT_HTTP_CONFIG,
  timeout: 600000, // 10 minutes for streaming
};

/**
 * Configuration for file upload requests (longer timeout)
 */
export const UPLOAD_HTTP_CONFIG: Omit<HttpClientConfig, "baseURL"> = {
  ...DEFAULT_HTTP_CONFIG,
  timeout: 300000, // 5 minutes for file uploads
  headers: {
    // Content-Type will be set by browser for multipart/form-data
  },
};

/**
 * Get configuration for specific request types
 */
export function getConfigForType(
  type: "default" | "streaming" | "upload"
): HttpClientConfig {
  const baseConfig = getHttpClientConfig();

  switch (type) {
    case "streaming":
      return {
        ...baseConfig,
        ...STREAMING_HTTP_CONFIG,
        // Use streaming-specific URL to bypass proxy buffering
        baseURL: apiConfigUtils.getStreamingEndpointUrl(""),
      };
    case "upload":
      return {
        ...baseConfig,
        ...UPLOAD_HTTP_CONFIG,
        baseURL: baseConfig.baseURL,
      };
    default:
      return baseConfig;
  }
}
